import { Constants } from '@pwc/pwc-deals-web-common';
import { isString } from 'lodash';
import qs from 'query-string';
import {
  ContentServiceHttpClient,
  CreateFFAHttpClient,
  ProjectHttpClient,
} from '../../common/services/httpClient';

export const AnalysisTypes = {
  Unstructured: 100,
  [Constants.QUALITY_OF_EARNINGS]: 200,
  [Constants.NET_WORKING_CAPITAL]: 300,
  [Constants.DEBT_AND_DEBT_LIKE]: 400,
};
Object.freeze(AnalysisTypes);

const baseServicePath = (projectId, analysisThreadId, versionStatus) => {
  const pathForId = analysisThreadId ? `/${analysisThreadId}` : '';
  const pathForStatus = versionStatus ? `/${versionStatus}` : '';

  return `projects/${projectId}/analyses${pathForId}${pathForStatus}`;
};

export const getAnalyses = async (projectId) => {
  const response = await ProjectHttpClient.get(baseServicePath(projectId));
  return response.data;
};

export const getAllAnalyses = async (projectId, tagIds) => {
  const response = await ContentServiceHttpClient.get(
    `/projects/${projectId}/analyses${
      tagIds ? `?${qs.stringify({ TagIds: tagIds })}` : ''
    }`
  );
  return response.data;
};

export const getPagedSortedAnalyses = async (
  projectId,
  page = 0,
  pageSize = 10,
  sortKey = 'sortOrder',
  isDescending = false,
  tagIds
) => {
  const response = await ContentServiceHttpClient.get(
    `/projects/${projectId}/analyses?currentPage=${page}&pageSize=${pageSize}&sortBy=${sortKey}&sortDescending=${isDescending.toString()}
      ${tagIds ? `&${qs.stringify({ TagIds: tagIds })}` : ''}
    `
  );
  return response.data;
};

export const getPagedAnalyses = async (projectId, page = 0, pageSize = 10) => {
  const response = await ContentServiceHttpClient.get(
    `/projects/${projectId}/analyses?currentPage=${page}&pageSize=${pageSize}`
  );
  return response.data;
};

export const getAnalysis = async (
  projectId,
  analysisThreadId,
  versionStatus
) => {
  const response = await ProjectHttpClient.get(
    `${baseServicePath(projectId, analysisThreadId, versionStatus)}`
  );
  return response.data;
};

export const getAnalysisDetailsStatus = async (projectId, analysisThreadId) => {
  const response = await ContentServiceHttpClient.get(
    `/projects/${projectId}/contents/${analysisThreadId}/status`
  );
  return response.data;
};

export const getAnalysisDetailsVersions = async (
  projectId,
  analysisThreadId
) => {
  const response = await ContentServiceHttpClient.get(
    `/projects/${projectId}/contents/${analysisThreadId}/versions`
  );
  return response.data;
};

export const getAnalysisDetailsDraft = async (projectId, analysisThreadId) => {
  const response = await ContentServiceHttpClient.get(
    `/projects/${projectId}/contents/${analysisThreadId}/draft`
  );
  return response.data;
};

export const getAnalysisDetailsShared = async (
  projectId,
  analysisContentId
) => {
  const response = await ContentServiceHttpClient.get(
    `/projects/${projectId}/contents/${analysisContentId}/shared`
  );
  return response.data;
};

export const getAnalysisVersions = async (projectId, analysisThreadId) => {
  const response = await ProjectHttpClient.get(
    `${baseServicePath(projectId, analysisThreadId)}/versions`
  );

  return response.data;
};

export const getAnalysisVersionsPrevious = (projectId, contentId) =>
  CreateFFAHttpClient(projectId)
    .get(`/projects/${projectId}/analysis/${contentId}/previousversion`)
    .then(({ data }) => data);

export const postAnalysis = async (projectId, analysisDto) => {
  const response = await ContentServiceHttpClient.post(
    `/projects/${projectId}/FreeFormAnalyses`,
    {
      ...analysisDto,
      createdBy: 'unset',
    }
  );

  return response.data;
};

export const putAnalysis = async (
  projectId,
  analysisContentId,
  analysisDto
) => {
  const modifiedData = {
    ...analysisDto,
    sections: analysisDto.sections.map((section) => {
      return {
        ...section,
        items: section.items.map((item) => {
          const isDataString = isString(item.data);
          if (isDataString) {
            return item;
          }
          return { ...item, data: JSON.stringify(item.data) };
        }),
      };
    }),
  };
  const response = await ContentServiceHttpClient.put(
    `/projects/${projectId}/FreeFormAnalyses/${analysisContentId}`,
    modifiedData
  );
  return response.data;
};

export const deleteAnalysis = async (projectId, analysisThreadId) => {
  const response = await ContentServiceHttpClient.delete(
    `/projects/${projectId}/contents/${analysisThreadId}`
  );
  return response;
};

export const updateAnalysisStatus = async (
  projectId,
  analysisThreadId,
  newStatus
) => {
  const url = `${baseServicePath(projectId, analysisThreadId)}/${newStatus}`;
  return ProjectHttpClient.post(url);
};

export const updateAnalysesOrder = async (projectId, sortChangeArray) => {
  const response = await ContentServiceHttpClient.post(
    `/projects/${projectId}/analyses/sortorder/v2`,
    sortChangeArray
  );

  return response.data;
};

export const updateAnalysisWorkflowStatus = async (
  projectId,
  contentId,
  workflowAction
) => {
  const response = await ContentServiceHttpClient.post(
    `projects/${projectId}/workflow/${contentId}/${workflowAction}`
  );
  return response.data;
};

export const projectsContentsSearch = async (projectId, data) => {
  const response = await ContentServiceHttpClient.post(
    `projects/${projectId}/Contents/Search`,
    data
  );
  return response.data;
};
